import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { FaBoxes, FaExclamationTriangle, FaLock, FaRoute } from 'react-icons/fa';

import Loading from '../../../../components/Loading';
import { useAllPdvs } from '../../../../hooks/useAllPdvs';
import {
   getTransferOrder,
   updateTransferOrder,
} from '../../../../services/inventory/transfersApi';
import { notifyApiError } from '../../../../utils/feedback';

import { Container } from '../../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../../../styles/layout/PageHeader';
import {
   Section as LayoutSection,
   SectionBody,
} from '../../../../styles/layout/Section';
import {
   AsideActions,
   AsideCard,
   AsideChecklist,
   AsideRow,
   ComposerPageGrid,
   Field,
   FieldError,
   FieldHint,
   FieldLabel,
   FieldSelect,
   FieldTextarea,
   FormCard,
   FormGrid,
   Section,
   SectionHead,
   SectionIcon,
   SectionTitles,
} from '../../../../styles/ui/Composer';
import {
   GhostButton,
   GhostOnDarkButton,
   PrimaryButton,
} from '../../../../styles/ui/Button';
import { EmptyState } from '../../../../styles/feedback/EmptyState';

import CheckLine from '../components/CheckLine';
import ReviewChangesModal from '../components/ReviewChangesModal';
import TransferItemsEditor from '../components/TransferItemsEditor';
import { OrderStatusBadge, RoutePill } from '../components/StatusBadges';
import { useOriginAvailability } from '../hooks/useOriginAvailability';
import { ORDER_STATUS, SHIPMENT_STATUS } from '../constants';
import { buildEditDiff, predictsOrderClose } from '../utils/editDiff';
import { itemErrorsByProdId } from '../utils/itemErrors';
import { formatDateTimeBr } from '../utils/format';

// ---------------------------------------------------------------------------
// Edição do PEDIDO da ordem (PUT /transfer-orders/:id).
//
// `items` é o ESTADO COMPLETO desejado (item ausente = remoção), mesmo padrão
// do editor de Movements — por isso o salvar tem DOIS passos desde 2026-09-10
// (fase 2 da refatoração visual, decisão F3): "Salvar alterações" abre o modal
// de revisão com o diff (alteradas/incluídos/removidos) e só o confirmar de lá
// dispara o PUT.
//
// Editabilidade segue o CONTRATO, não o protótipo (decisão F1 — o protótipo
// travava tudo): origem nunca muda; destino muda SÓ antes da primeira remessa;
// observações sempre editáveis. As travas de item aparecem antes do servidor:
// `requested_qtd` nunca abaixo do já expedido (validação por linha no editor)
// e item com expedição não é removível.
//
// Esta é também a única forma de "fechar mandando menos": reduzir o pedido até
// o expedido JÁ CONFERIDO satisfaz a contagem e o backend fecha a ordem
// sozinho. A previsão (`predictsOrderClose`) avisa no checklist e no modal; a
// autoridade é o `status_transition` da resposta.
// ---------------------------------------------------------------------------

const HeaderBlock = styled.div`
   display: grid;
   gap: 10px;
   justify-items: start;
`;

const TitleRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
`;

// Valor somente leitura no lugar de um campo — o que não é editável vira
// TEXTO, não input desabilitado.
const ReadValue = styled.div`
   min-height: 40px;
   display: flex;
   align-items: center;
   font-size: 14px;
   font-weight: 600;
   color: ${({ theme }) => theme.colors.textPrimary};
   overflow-wrap: anywhere;
`;

const ErrorStrip = styled.p`
   margin: 12px 0 0;
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid rgba(220, 38, 38, 0.22);
   background: rgba(220, 38, 38, 0.06);
   font-size: 12.5px;
   font-weight: 700;
   color: #8e1b1b;
`;

// Conflito de versão otimista (handoff 0107): outro usuário mutou a ordem
// entre o GET e o salvar. Fundo OPACO — a faixa senta no gradiente escuro.
const ConflictBanner = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 12px;
   margin-bottom: 16px;
   padding: 12px 14px;
   border-radius: 10px;
   border: 1px solid #ecd9ab;
   background: #fdf6e3;

   .ic {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border-radius: 8px;
      background: rgba(217, 119, 6, 0.14);
      color: #8a5a00;
   }
   .txt {
      flex: 1;
      min-width: 0;
      font-size: 12.5px;
      line-height: 1.5;
      color: #6b4600;

      strong {
         color: #8a5a00;
      }
   }
`;

const DismissBtn = styled.button`
   appearance: none;
   flex-shrink: 0;
   border: 0;
   width: 26px;
   height: 26px;
   border-radius: 7px;
   background: transparent;
   color: #8a5a00;
   font-size: 15px;
   line-height: 1;
   cursor: pointer;

   /* G.4.1: hover precisa declarar background, senão herda o azul do legado. */
   &:hover:not(:disabled) {
      background: rgba(217, 119, 6, 0.14);
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
`;

const seedItems = (order) =>
   (order?.items ?? []).map((item) => ({
      prod_id: item.prod_id,
      prod_sku: item.prod_sku,
      prod_name: item.prod_name,
      requested_qtd: item.requested_qtd,
   }));

export default function TransferOrderEdit() {
   const { id } = useParams();
   const navigate = useNavigate();
   const location = useLocation();

   const { pdvs: allPdvs } = useAllPdvs();

   const [order, setOrder] = useState(null);
   const [isLoading, setIsLoading] = useState(true);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [fieldErrors, setFieldErrors] = useState({});
   const [reviewOpen, setReviewOpen] = useState(false);
   // Conflito de versão (handoff 0107): { name, at } de quem mutou, quando o
   // backend souber dizer — ambos podem vir null (mutação por job).
   const [conflict, setConflict] = useState(null);

   const [destPdvId, setDestPdvId] = useState('');
   const [notes, setNotes] = useState('');
   const [items, setItems] = useState([]);

   const availability = useOriginAvailability(order?.origin_pdv_id);

   // `seed` = true reseta o rascunho para o estado carregado (abertura da tela
   // e conflito de versão — decisão D-A); false recarrega SÓ a ordem (pisos e
   // acumuladores frescos) preservando o que o operador digitou (422 de itens).
   const loadOrder = useCallback(
      async (seed) => {
         setIsLoading(true);
         try {
            const result = await getTransferOrder(id);
            setOrder(result);
            if (seed) {
               setDestPdvId(String(result?.dest_pdv_id ?? ''));
               setNotes(result?.notes ?? '');
               setItems(seedItems(result));
            }
            return result;
         } catch (err) {
            setOrder(null);
            notifyApiError(err, {
               fallback: 'Erro ao carregar a transferência.',
            });
            return null;
         } finally {
            setIsLoading(false);
         }
      },
      [id]
   );

   useEffect(() => {
      loadOrder(true);
   }, [loadOrder]);

   // Pisos por produto: quanto já saiu da origem. O editor valida a quantidade
   // e trava a remoção com base neles.
   const shippedByProdId = useMemo(
      () =>
         Object.fromEntries(
            (order?.items ?? []).map((item) => [
               item.prod_id,
               Number(item.shipped_qtd ?? 0),
            ])
         ),
      [order]
   );

   // Quantidades originais — ligam o modo edição do editor (tags/notas) e
   // alimentam o diff.
   const originalByProdId = useMemo(
      () =>
         Object.fromEntries(
            (order?.items ?? []).map((item) => [
               item.prod_id,
               Number(item.requested_qtd ?? 0),
            ])
         ),
      [order]
   );

   const originalItems = useMemo(() => order?.items ?? [], [order]);

   const diff = useMemo(
      () => buildEditDiff(originalItems, items),
      [originalItems, items]
   );
   const closesOrder = useMemo(
      () => predictsOrderClose(items, originalItems, diff.total),
      [items, originalItems, diff.total]
   );

   // C7 (handoff 0107): o destino trava com a primeira remessa NÃO-CANCELADA
   // (`transfer_dest_locked`) — remessa cancelada devolveu o estoque e não
   // trava nada. Antes travávamos com qualquer remessa, mais que o contrato.
   const hasActiveShipment = (order?.shipments ?? []).some(
      (shipment) => shipment.status !== SHIPMENT_STATUS.CANCELLED
   );

   const isEditable =
      order?.status === ORDER_STATUS.OPEN ||
      order?.status === ORDER_STATUS.PARTIAL;

   const destOptions = allPdvs.filter(
      (pdv) => String(pdv.id) !== String(order?.origin_pdv_id)
   );

   // Erros por linha (mesma régua do editor): quantidade inválida ou abaixo do
   // piso do expedido — o servidor recusaria (422).
   const rowErrorCount = items.filter((item) => {
      const qty = Number(item.requested_qtd || 0);
      const invalid =
         String(item.requested_qtd ?? '').trim() === '' || qty < 1;
      return invalid || qty < Number(shippedByProdId[item.prod_id] ?? 0);
   }).length;

   const lockedCount = items.filter(
      (item) => Number(shippedByProdId[item.prod_id] ?? 0) > 0
   ).length;

   const totalUnits = items.reduce(
      (acc, item) => acc + (Number(item.requested_qtd) || 0),
      0
   );

   const canSave =
      items.length > 0 && rowErrorCount === 0 && diff.total > 0 && !isSubmitting;

   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate(`/inventory/transfers/${id}`);
   };

   const handleDiscard = () => {
      setDestPdvId(String(order?.dest_pdv_id ?? ''));
      setNotes(order?.notes ?? '');
      setItems(seedItems(order));
      setFieldErrors({});
      toast.info('Alterações descartadas.');
   };

   const handleSubmit = async () => {
      if (!canSave) return;

      setIsSubmitting(true);
      setFieldErrors({});
      setConflict(null);
      try {
         const result = await updateTransferOrder(id, {
            // Versão otimista (0107): eco do GET que abriu a tela — o backend
            // recusa com 409 se alguém mutou a ordem nesse meio-tempo.
            version: order?.version,
            notes,
            // Destino só entra no payload quando ainda é editável — mandar o
            // campo com remessa ativa seria pedir um 422 à toa.
            destPdvId: hasActiveShipment ? undefined : destPdvId,
            items: items.map((item) => ({
               prodId: item.prod_id,
               requestedQtd: Number(item.requested_qtd),
            })),
         });

         toast.success(result.message);
         if (result.statusTransition?.to === ORDER_STATUS.CLOSED) {
            toast.info(
               'O pedido passou a coincidir com o conferido — a transferência foi concluída.'
            );
         }
         navigate(`/inventory/transfers/${id}`, { replace: true });
      } catch (err) {
         // Fecha a revisão para o feedback ficar visível na tela.
         setReviewOpen(false);

         // 409 de versão: outro usuário mutou a ordem. Banner + reload com
         // RESET do rascunho (decisão D-A) — o operador revê sobre o estado
         // novo. Sem toast: o banner é o feedback.
         if (err?.code === 'transfer_version_conflict') {
            const entry =
               (err.errorItems ?? []).find(
                  (item) => item.code === 'transfer_version_conflict'
               ) ?? {};
            setConflict({
               name: entry.changed_by?.name ?? null,
               userId: entry.changed_by?.id ?? null,
               at: entry.changed_at ?? null,
            });
            await loadOrder(true);
            return;
         }

         setFieldErrors(err?.fieldErrors ?? {});

         // 422 estruturado de itens (0107 §5.1): recarrega a ordem MANTENDO o
         // rascunho — os pisos frescos marcam as linhas com o mínimo
         // verdadeiro. Item removido que ganhou expedição volta à lista
         // (travado): ele não pode sair do pedido.
         const violations = itemErrorsByProdId(err);
         if (Object.keys(violations).length > 0) {
            const fresh = await loadOrder(false);
            const restored = (fresh?.items ?? []).filter(
               (freshItem) =>
                  violations[freshItem.prod_id]?.code ===
                     'transfer_item_has_shipments' &&
                  !items.some(
                     (draft) =>
                        String(draft.prod_id) === String(freshItem.prod_id)
                  )
            );
            if (restored.length > 0) {
               setItems((prev) => [...prev, ...seedItems({ items: restored })]);
            }
         }

         notifyApiError(err, {
            fallback: 'Não foi possível salvar a transferência.',
         });
      } finally {
         setIsSubmitting(false);
      }
   };

   if (!isLoading && !isEditable) {
      return (
         <Container>
            <PageHeader>
               <PageHeaderText>
                  <HeaderBlock>
                     <GhostOnDarkButton type="button" onClick={handleBack}>
                        ← Voltar para o pedido
                     </GhostOnDarkButton>
                     <PageTitle>Editar pedido #{id}</PageTitle>
                  </HeaderBlock>
               </PageHeaderText>
            </PageHeader>
            <LayoutSection>
               <SectionBody>
                  <EmptyState
                     title={
                        order
                           ? 'Transferência não editável'
                           : 'Transferência não encontrada'
                     }
                     description={
                        order
                           ? 'Só transferências abertas ou parciais podem ter o pedido alterado.'
                           : 'Verifique o identificador, ou se você tem acesso a um dos PDVs envolvidos.'
                     }
                  />
               </SectionBody>
            </LayoutSection>
         </Container>
      );
   }

   const changeHeadline =
      diff.total === 0
         ? 'Nenhuma alteração'
         : `${diff.total} ${diff.total === 1 ? 'alteração' : 'alterações'}`;

   // "(44 - Bruno Lima em 10/09/2026 17:22)" — cada pedaço pode faltar
   // (mutação por job/webhook chega sem autor).
   let conflictWho = '';
   if (conflict?.name) {
      const idPrefix = conflict.userId != null ? `${conflict.userId} - ` : '';
      const when = conflict.at ? ` em ${formatDateTimeBr(conflict.at)}` : '';
      conflictWho = ` (${idPrefix}${conflict.name}${when})`;
   }

   return (
      <Container>
         <Loading isLoading={isLoading || isSubmitting} />

         <PageHeader>
            <PageHeaderText>
               <HeaderBlock>
                  <GhostOnDarkButton type="button" onClick={handleBack}>
                     ← Voltar para o pedido
                  </GhostOnDarkButton>
                  <TitleRow>
                     <PageTitle>Editar pedido #{order?.id ?? id}</PageTitle>
                     <OrderStatusBadge value={order?.status} onDark />
                     <RoutePill
                        originName={order?.origin_pdv_name}
                        destName={order?.dest_pdv_name}
                     />
                  </TitleRow>
                  <PageSubtitle>
                     A lista abaixo substitui integralmente os itens do pedido.
                     Item ausente é removido.
                  </PageSubtitle>
               </HeaderBlock>
            </PageHeaderText>
            <PageHeaderActions>
               <GhostOnDarkButton
                  type="button"
                  onClick={handleBack}
                  disabled={isSubmitting}
               >
                  Cancelar edição
               </GhostOnDarkButton>
            </PageHeaderActions>
         </PageHeader>

         {conflict && (
            <ConflictBanner role="status">
               <span className="ic" aria-hidden="true">
                  <FaExclamationTriangle size={13} />
               </span>
               <span className="txt">
                  <strong>O pedido foi alterado por outro usuário</strong>
                  {conflictWho} — os dados foram recarregados e as suas
                  alterações descartadas. Revise sobre o estado atual antes de
                  salvar.
               </span>
               <DismissBtn
                  type="button"
                  onClick={() => setConflict(null)}
                  aria-label="Dispensar aviso"
               >
                  ×
               </DismissBtn>
            </ConflictBanner>
         )}

         <ComposerPageGrid>
            <FormCard>
               <Section>
                  <SectionHead>
                     <SectionIcon>
                        <FaRoute size={15} />
                        <span className="step">1</span>
                     </SectionIcon>
                     <SectionTitles>
                        <div className="t">Rota</div>
                        <div className="d">
                           Origem não muda; destino só antes da primeira remessa
                        </div>
                     </SectionTitles>
                  </SectionHead>

                  <FormGrid>
                     <Field $span={6}>
                        <FieldLabel as="span">Origem</FieldLabel>
                        <ReadValue>{order?.origin_pdv_name ?? '—'}</ReadValue>
                     </Field>

                     <Field $span={6}>
                        <FieldLabel htmlFor="edit-dest-pdv">Destino</FieldLabel>
                        {hasActiveShipment ? (
                           <>
                              <ReadValue>
                                 {order?.dest_pdv_name ?? '—'}
                              </ReadValue>
                              <FieldHint>
                                 <FaLock size={9} aria-hidden="true" /> O
                                 destino não muda depois da primeira remessa.
                              </FieldHint>
                           </>
                        ) : (
                           <>
                              <FieldSelect
                                 id="edit-dest-pdv"
                                 value={destPdvId}
                                 disabled={isSubmitting}
                                 $invalid={!!fieldErrors.dest_pdv_id}
                                 onChange={(event) =>
                                    setDestPdvId(event.target.value)
                                 }
                              >
                                 {destOptions.map((pdv) => (
                                    <option key={pdv.id} value={pdv.id}>
                                       {pdv.pdv_name ??
                                          pdv.name ??
                                          `PDV ${pdv.id}`}
                                    </option>
                                 ))}
                              </FieldSelect>
                              {fieldErrors.dest_pdv_id && (
                                 <FieldError>
                                    {fieldErrors.dest_pdv_id}
                                 </FieldError>
                              )}
                           </>
                        )}
                     </Field>

                     <Field $span={6}>
                        <FieldLabel as="span">Situação</FieldLabel>
                        <ReadValue>
                           {order?.status === ORDER_STATUS.PARTIAL
                              ? 'Parcial'
                              : 'Aberta'}
                        </ReadValue>
                     </Field>

                     <Field $span={6}>
                        <FieldLabel as="span">Criado por</FieldLabel>
                        <ReadValue>{order?.created_by_name ?? '—'}</ReadValue>
                     </Field>

                     <Field $span={12}>
                        <FieldLabel htmlFor="edit-notes">
                           Observações
                        </FieldLabel>
                        <FieldTextarea
                           id="edit-notes"
                           value={notes}
                           disabled={isSubmitting}
                           placeholder="Opcional"
                           onChange={(event) => setNotes(event.target.value)}
                        />
                        {fieldErrors.notes && (
                           <FieldError>{fieldErrors.notes}</FieldError>
                        )}
                     </Field>
                  </FormGrid>
               </Section>

               <Section>
                  <SectionHead>
                     <SectionIcon>
                        <FaBoxes size={15} />
                        <span className="step">2</span>
                     </SectionIcon>
                     <SectionTitles>
                        <div className="t">Itens</div>
                        <div className="d">
                           {items.length
                              ? `${items.length} ${items.length === 1 ? 'item' : 'itens'}`
                              : 'O pedido precisa de ao menos um item'}
                        </div>
                     </SectionTitles>
                  </SectionHead>

                  <TransferItemsEditor
                     items={items}
                     onChange={setItems}
                     shippedByProdId={shippedByProdId}
                     originalByProdId={originalByProdId}
                     availabilityByProdId={availability}
                     disabled={isSubmitting}
                     emptyDescription="O pedido precisa de ao menos um item."
                  />

                  {rowErrorCount > 0 && (
                     <ErrorStrip>
                        Há itens fora dos limites. Corrija antes de salvar.
                     </ErrorStrip>
                  )}
                  {fieldErrors.items && (
                     <FieldError style={{ marginTop: 10 }}>
                        {fieldErrors.items}
                     </FieldError>
                  )}
               </Section>
            </FormCard>

            <AsideCard>
               <h2>{changeHeadline}</h2>

               <AsideRow>
                  <span className="lbl">Itens no pedido</span>
                  <span className="val">{items.length}</span>
               </AsideRow>
               <AsideRow>
                  <span className="lbl">Unidades solicitadas</span>
                  <span className="val">{totalUnits} un</span>
               </AsideRow>
               <AsideRow>
                  <span className="lbl">Quantidade alterada</span>
                  <span
                     className="val"
                     style={diff.changed.length ? { color: '#8a5a00' } : undefined}
                  >
                     {diff.changed.length}
                  </span>
               </AsideRow>
               <AsideRow>
                  <span className="lbl">Incluídos</span>
                  <span
                     className="val"
                     style={diff.added.length ? { color: '#0b3a63' } : undefined}
                  >
                     {diff.added.length}
                  </span>
               </AsideRow>
               <AsideRow>
                  <span className="lbl">Removidos</span>
                  <span
                     className="val"
                     style={diff.removed.length ? { color: '#c62828' } : undefined}
                  >
                     {diff.removed.length}
                  </span>
               </AsideRow>

               <AsideChecklist>
                  <CheckLine ok={items.length > 0}>
                     Pedido com ao menos um item
                  </CheckLine>
                  <CheckLine ok={rowErrorCount === 0}>
                     {rowErrorCount === 0
                        ? 'Quantidades dentro do piso do expedido'
                        : 'Há quantidade inválida ou abaixo do já expedido'}
                  </CheckLine>
                  {lockedCount > 0 && (
                     <CheckLine ok>
                        {lockedCount} item(ns) com expedição — quantidade
                        travada no piso e sem remoção
                     </CheckLine>
                  )}
                  {closesOrder && (
                     <CheckLine ok={false}>
                        A redução encerra a ordem ao salvar
                     </CheckLine>
                  )}
               </AsideChecklist>

               <AsideActions>
                  {/* Azul, mas quem ESCREVE é o confirmar do modal de revisão
                      (F3) — este botão é o único caminho até lá e carrega a
                      intenção da escrita. */}
                  <PrimaryButton
                     type="button"
                     disabled={!canSave}
                     onClick={() => setReviewOpen(true)}
                  >
                     Salvar alterações
                  </PrimaryButton>
                  <GhostButton
                     type="button"
                     onClick={handleDiscard}
                     disabled={diff.total === 0 || isSubmitting}
                  >
                     Descartar alterações
                  </GhostButton>
               </AsideActions>
            </AsideCard>
         </ComposerPageGrid>

         <ReviewChangesModal
            isOpen={reviewOpen}
            order={order}
            diff={diff}
            closesOrder={closesOrder}
            isSubmitting={isSubmitting}
            onCancel={() => setReviewOpen(false)}
            onConfirm={handleSubmit}
         />
      </Container>
   );
}
